import {
  Badge,
  Box,
  Button,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { NodeProps, Position } from '@xyflow/react'
import React, { useMemo, useState } from 'react'

import type { Pbx } from '../../api/types'
import { DayNightNodeData } from '../../converters/shared/reactFlowTypes'
import { useUpdatePbx } from '../../queries/hooks'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { useDayNightSettings } from '../Canvas/SwitchboardCanvas'
import { NodeSelect } from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useDayNightNode } from './hooks/useDayNightNode'

export interface DayNightNodeProps extends NodeProps {
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void

  showDeleteButton?: boolean
}

// Toggle codes for Day/Night (Basic mode dropdown options)
// Display shows *30-*39, but values stored are 30-39 (without asterisk)
const TOGGLE_CODES = [
  '30',
  '31',
  '32',
  '33',
  '34',
  '35',
  '36',
  '37',
  '38',
  '39',
]

// Store mode state outside component to persist across re-renders
const modeStateMap = new Map<string, 'basic' | 'advanced'>()

const DayNightNodeComponent: React.FC<DayNightNodeProps> = ({
  id,
  data,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const nodeData = data as DayNightNodeData & Record<string, unknown>
  const t = useSwitchboardT()
  const { api, pbxId, toast } = useDayNightSettings()
  const updatePbxMutation = useUpdatePbx({ api })
  const queryClient = useQueryClient()

  // Infer mode from toggleCode: null = Advanced mode, value = Basic mode
  const inferredMode: 'basic' | 'advanced' =
    nodeData.toggleCode === null || nodeData.toggleCode === undefined
      ? 'advanced'
      : 'basic'

  // Use state that initializes from the persistent map or inferred mode
  const [mode, setMode] = useState<'basic' | 'advanced'>(() => {
    return modeStateMap.get(id) || inferredMode
  })
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

  // Sync state to the persistent map whenever it changes
  React.useEffect(() => {
    modeStateMap.set(id, mode)
  }, [mode, id])

  // Update mode when toggleCode changes from external source
  React.useEffect(() => {
    const newMode =
      nodeData.toggleCode === null || nodeData.toggleCode === undefined
        ? 'advanced'
        : 'basic'
    if (newMode !== mode) {
      setMode(newMode)
      modeStateMap.set(id, newMode)
    }
  }, [nodeData.toggleCode, id, mode])

  const {
    currentToggleCode,
    currentStatus,
    handleToggleCodeChange,
    handleStatusChange,
    hasData,
  } = useDayNightNode(id, nodeData)

  const handleToggleCodeSelectChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value // Value is "30"-"39" (without asterisk)
      // Convert to display format: "30" -> "*30"
      handleToggleCodeChange(`*${value}`)
    },
    [handleToggleCodeChange],
  )

  const handleStatusClick = React.useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isUpdatingStatus) return

      const newStatus = currentStatus === 'open' ? 'closed' : 'open'

      // If PBX context is unavailable, fall back to local status update.
      if (!pbxId || !api) {
        handleStatusChange(newStatus)
        return
      }

      // Update PBX via API if pbxId is available
      setIsUpdatingStatus(true)
      try {
        // Determine field to update
        let fieldToUpdate: string
        const fieldValue = newStatus === 'open' ? '1' : '0'

        if (currentToggleCode === null || currentToggleCode === '*3') {
          // Advanced mode: update day_night field
          fieldToUpdate = 'day_night'
        } else {
          // Basic mode: extract last digit
          // "*37" → "7" → "day_night_7"
          const lastDigit = currentToggleCode.slice(-1)
          fieldToUpdate = `day_night_${lastDigit}`
        }

        // Get current PBX data from React Query cache to ensure we include all day_night fields
        const cachedPbx = queryClient.getQueryData<Pbx>(['pbx', pbxId])

        // Build update payload with all current day_night fields, then update the changed one
        const updatePayload: Record<string, '0' | '1'> = {}
        if (cachedPbx) {
          // Include all current day_night fields from cache
          if (cachedPbx.day_night) updatePayload.day_night = cachedPbx.day_night
          for (let i = 0; i <= 9; i++) {
            const field = `day_night_${i}` as keyof Pbx
            if (cachedPbx[field]) {
              updatePayload[field] = cachedPbx[field] as '0' | '1'
            }
          }
        }
        // Override with the field we're updating
        updatePayload[fieldToUpdate] = fieldValue

        const mutationPayload = {
          pbxId,
          ...updatePayload,
        }

        const result = await updatePbxMutation.mutateAsync(mutationPayload)

        // Update the cache with the response to keep it in sync
        // Note: result is already normalized (booleans → strings) by the API client
        queryClient.setQueryData<Pbx>(['pbx', pbxId], result)

        // Show success toast (matching legacy message)
        toast?.create({
          title: text(
            t,
            'switchboard.nodes.dayNight.toastSuccess',
            'PBX DAY/NIGHT status updated successfully',
          ),
          type: 'success',
        })
      } catch (error) {
        // Show error toast
        const errorMessage =
          error instanceof Error
            ? error.message
            : error && typeof error === 'object' && 'message' in error
              ? String(error.message)
              : text(
                  t,
                  'switchboard.nodes.dayNight.toastFailed',
                  'Failed to update PBX day/night status',
                )
        toast?.create({
          title: text(
            t,
            'switchboard.nodes.dayNight.toastFailed',
            'Failed to update PBX day/night status',
          ),
          description: errorMessage,
          type: 'error',
        })
      } finally {
        setIsUpdatingStatus(false)
      }
    },
    [
      currentStatus,
      isUpdatingStatus,
      currentToggleCode,
      handleStatusChange,
      pbxId,
      updatePbxMutation,
      toast,
      queryClient,
      t,
    ],
  )

  const handleModeToggle = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (mode === 'basic') {
        // Switching to Advanced mode: set toggleCode to null
        setMode('advanced')
        handleToggleCodeChange(null)
      } else {
        // Switching to Basic mode: set default code to *30
        setMode('basic')
        handleToggleCodeChange('*30')
      }
    },
    [mode, handleToggleCodeChange],
  )

  // Extract numeric code from toggleCode for dropdown value (remove asterisk)
  const currentCodeValue =
    currentToggleCode && currentToggleCode.startsWith('*')
      ? currentToggleCode.slice(1)
      : currentToggleCode

  // Add two custom handles on the right side: When Open (top), When Closed (bottom)
  // Handles positioned at top and bottom corners
  // Memoize handles array to prevent re-creating on every render
  const dayNightHandles = useMemo(
    () => [
      {
        id: 'whenOpen',
        position: Position.Right as Position,
        style: {
          top: '0px', // Handle top at 0px, center at 7px, connection point at ~0%
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.dayNight.whenOpen', 'When Open'),
      },
      {
        id: 'whenClosed',
        position: Position.Right as Position,
        style: {
          top: 'calc(100%)', // Handle top at 100%, center at 100% - 7px, connection point at ~100%
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.dayNight.whenClosed', 'When Closed'),
      },
    ],
    [t],
  )

  return (
    <BaseNode
      label=" "
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
      sourceHandlePosition={null}
      additionalSourceHandles={dayNightHandles}
    >
      <Box position="relative" mt={-1} mb={1}>
        {/* Top Row: DAY/NIGHT | OPEN/CLOSED - Positioned at absolute top */}
        <Box position="absolute" top={-10} left={0} right={0} px={4} zIndex={1}>
          <HStack justify="flex-start" align="center" gap={2} pr={6}>
            <Text fontSize="sm" fontWeight="semibold" color="fg.default">
              {text(t, 'switchboard.nodeLabels.day_night', 'DAY/NIGHT')}
            </Text>
            <Badge
              colorPalette={currentStatus === 'open' ? 'success' : 'error'}
              variant="solid"
              fontSize="xs"
              px={2}
              py={0.5}
              borderRadius="md"
              cursor={isUpdatingStatus ? 'progress' : 'pointer'}
              onClick={handleStatusClick}
              onMouseDown={(e) => e.stopPropagation()}
              pointerEvents={isUpdatingStatus ? 'none' : 'auto'}
            >
              <HStack as="span" gap={1} align="center">
                {isUpdatingStatus && <Spinner size="xs" color="currentColor" />}
                <Text as="span" fontSize="inherit" lineHeight="inherit">
                  {currentStatus === 'open'
                    ? text(t, 'switchboard.nodes.dayNight.open', 'OPEN')
                    : text(t, 'switchboard.nodes.dayNight.closed', 'CLOSED')}
                </Text>
              </HStack>
            </Badge>
          </HStack>
        </Box>
        <VStack align="stretch" gap={0.5} pt={1}>
          {/* Compact row: Toggle instruction and mode toggle */}
          <HStack justify="space-between" align="center" gap={1}>
            <Text fontSize="xs" color="fg.muted">
              {text(
                t,
                'switchboard.nodes.dayNight.pressToToggle',
                '(Press {{code}} to Toggle)',
                {
                  code: mode === 'advanced' ? '*3' : currentToggleCode || '*30',
                },
              )}
            </Text>
            <Button
              type="button"
              variant="subtle"
              size="xs"
              colorPalette="info"
              bg="blue.subtle"
              color="blue.fg"
              px={1}
              py={0}
              height="auto"
              minH="auto"
              fontSize="xs"
              fontWeight="normal"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleModeToggle(e)
              }}
              _hover={{
                bg: 'blue.muted',
              }}
            >
              {mode === 'basic'
                ? text(t, 'switchboard.nodes.dayNight.basic', 'Basic')
                : text(t, 'switchboard.nodes.dayNight.advanced', 'Advanced')}
            </Button>
          </HStack>

          {/* Basic Mode - Toggle Code Selector (only shown when in Basic mode) */}
          {mode === 'basic' && (
            <Box>
              <NodeSelect
                label={text(
                  t,
                  'switchboard.nodes.dayNight.toggleCode',
                  'Toggle Code',
                )}
                value={currentCodeValue || '30'}
                onChange={handleToggleCodeSelectChange}
              >
                {TOGGLE_CODES.map((code) => (
                  <option key={code} value={code}>
                    *{code}
                  </option>
                ))}
              </NodeSelect>
            </Box>
          )}
        </VStack>
      </Box>
    </BaseNode>
  )
}

export const DayNightNode = React.memo(DayNightNodeComponent, nodePropsAreEqual)
